import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isGuestUser } from '../constants/auth';

export default function DeleteAccountPanel({ compact = false }) {
  const navigate = useNavigate();
  const { user, deleteAccount } = useAuth();
  const isGuest = isGuestUser(user);
  const socialOnly = Number(user?.password_enabled) === 0;

  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (isGuest) {
    if (compact) return null;
    return (
      <p style={S.guestNote}>
        ゲストアカウントは削除できません。退会する場合は先に登録してください。
      </p>
    );
  }

  const handleDelete = async () => {
    if (!password.trim() || (socialOnly && password.trim() !== '削除')) {
      setError(socialOnly ? '確認欄に「削除」と入力してください' : 'パスワードを入力してください');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await deleteAccount(socialOnly ? password.trim() : password);
      setShowConfirm(false);
      setPassword('');
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'アカウントの削除に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (!showConfirm) {
    return (
      <button data-control="row" data-control-id="DeleteAccountPanel-deleteBtn"
        type="button"
        style={{ ...S.deleteBtn, ...(compact ? S.deleteBtnCompact : {}) }}
        onClick={() => { setError(''); setShowConfirm(true); }}
      >
        アカウントを削除
      </button>
    );
  }

  return (
    <div style={S.confirmBox}>
      <p style={S.confirmText}>
        アカウントを削除すると、投稿・メッセージ・プロフィールなどすべてのデータが完全に消えます。この操作は取り消せません。
      </p>
      <label style={S.label}>
        {socialOnly ? '確認のため「削除」と入力' : '確認のためパスワードを入力'}
        <input
          type={socialOnly ? 'text' : 'password'}
          style={S.input}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={socialOnly ? 'off' : 'current-password'}
          placeholder={socialOnly ? '削除' : 'パスワード'}
        />
      </label>
      {socialOnly && <p style={S.confirmText}>外部サービスでログインしてから10分以内に削除できます。時間が経っている場合は、同じサービスでログインし直してください。</p>}
      {error ? <p style={S.error}>{error}</p> : null}
      <button data-control="primary" data-control-id="DeleteAccountPanel-confirmBtn"
        type="button"
        style={S.confirmBtn}
        onClick={handleDelete}
        disabled={loading}
      >
        {loading ? '削除中…' : '削除する'}
      </button>
      <button data-control="secondary" data-control-id="DeleteAccountPanel-cancelBtn"
        type="button"
        style={S.cancelBtn}
        onClick={() => { setShowConfirm(false); setPassword(''); setError(''); }}
        disabled={loading}
      >
        キャンセル
      </button>
    </div>
  );
}

const S = {
  guestNote: {
    margin: 0,
    fontSize: 12,
    color: 'var(--text-muted)',
    lineHeight: 1.55,
  },
  deleteBtn: {
    width: '100%',
    padding: '13px 0',
    borderRadius: 14,
    border: '1px solid var(--border-light)',
    background: 'transparent',
    color: 'var(--text-muted)',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  deleteBtnCompact: {
    marginTop: 8,
  },
  confirmBox: {
    marginTop: 4,
    padding: 16,
    borderRadius: 14,
    border: '1px solid rgba(239,68,68,0.35)',
    background: 'rgba(239,68,68,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  confirmText: {
    margin: 0,
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-muted)',
  },
  input: {
    padding: '11px 12px',
    borderRadius: 10,
    border: '1px solid var(--border-light)',
    background: 'var(--surface)',
    color: 'var(--text)',
    fontSize: 14,
    fontFamily: 'inherit',
  },
  error: { margin: 0, fontSize: 13, color: '#ff6b6b' },
  confirmBtn: {
    width: '100%',
    padding: '13px 0',
    borderRadius: 14,
    border: 'none',
    background: '#ef4444',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  cancelBtn: {
    width: '100%',
    padding: '11px 0',
    borderRadius: 14,
    border: '1px solid var(--border-light)',
    background: 'transparent',
    color: 'var(--text-muted)',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
};
